import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import {
  DEFAULT_REPORT_PRESET,
  ReportsPage,
  reportPresets,
  useReportsAvailability,
} from '@/features/reports';
import { ROUTES } from '@/lib/route-paths';

/**
 * Tab, window and branch live in the URL so a report can be linked to and
 * survives a reload.
 */
const reportsSearchSchema = z.object({
  tab: z.enum(['business', 'claire']).optional().catch(undefined),
  range: z
    .enum(reportPresets.map((p) => p.value) as [string, ...string[]])
    .optional()
    .catch(undefined),
  location: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute('/_authed/dashboard/reports')({
  validateSearch: reportsSearchSchema,
  component: ReportsRoute,
});

function ReportsRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // Behind `rollout-reports`: a clinic without it is sent home rather than
  // shown a page whose reports the API would refuse.
  const { reportsEnabled, isPending } = useReportsAvailability();
  if (isPending) return null;
  if (!reportsEnabled) return <Navigate to={ROUTES.dashboard} replace />;
  return (
    <ReportsPage
      tab={search.tab ?? 'business'}
      preset={
        (search.range as typeof DEFAULT_REPORT_PRESET) ?? DEFAULT_REPORT_PRESET
      }
      locationId={search.location}
      onChange={(next) =>
        navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })
      }
    />
  );
}
